import Accessories from "@components/steps/Accessories";
import ConfirmAuthenticatedContainer from "@components/steps/ConfirmAuthenticatedContainer";
import ConfirmUnAuthenticatedContainer from "@components/steps/ConfirmUnAuthenticatedContainer";
import ContactInfo from "@components/steps/ContactInfo";
import OtherCredentialsVerification from "@components/steps/OtherCredentialsVerification";
import ProfessionalInfo from "@components/steps/ProfessionalInfo";
import ShippingAddress from "@components/steps/ShippingAddress";
import { FOCUS_SUBSCRIPTIONS_SKUS, PRODUCTS } from "@constants/prices";
import type { ComponentType } from "react";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

// Utility function to check if cart contains physical products
export const hasPhysicalProducts = (items: { sku: string; quantity: number }[]) => {
  return items.some((item) => {
    const product = PRODUCTS[item.sku];
    return product?.type === "physicalProduct";
  });
};

// Step component type - using any because components have different prop interfaces
// The StepForm component handles prop passing correctly at runtime
type StepComponent = ComponentType<any>;

export interface UseStepFlowReturn {
  hasAccessoryStep: boolean;
  hasShippingStep: boolean;
  stepComponents: StepComponent[];
  stepLabels: string[];
  stepTitles: Record<string, string>;
  authenticatedStepTitles: Record<string, string>;
  currentStep: number;
  activeStepIndex: number;
  isOnConfirmStep: boolean;
}

export const useStepFlow = (isAuthenticated: boolean | undefined): UseStepFlowReturn => {
  const { watch } = useFormContext();

  const currentStep = watch("currentStep") || 1;
  const accessoryCategories = watch("accessoryCategories") || [];
  const accessories = watch("accessories") || {};
  const products: { sku: string; quantity: number }[] = watch("items") || [];
  const professionalCredentials = watch("professionalCredentials");

  const hasILSSubscription = useMemo(() => {
    return products.some((item) => FOCUS_SUBSCRIPTIONS_SKUS.includes(item.sku));
  }, [products]);

  const hasAccessoryStep = useMemo(() => {
    // Hide accessories step if cart contains any ILS subscription
    if (hasILSSubscription) return false;

    return accessoryCategories.length > 0;
  }, [accessoryCategories, hasILSSubscription]);

  const hasShippingStep = useMemo(() => {
    return Object.keys(accessories).length > 0 || hasPhysicalProducts(products);
  }, [accessories, products]);

  const hasOtherCredentialsStep = useMemo(() => {
    return professionalCredentials === "Other";
  }, [professionalCredentials]);

  const stepComponents = useMemo(() => {
    if (isAuthenticated === undefined) return [];
    const steps = [];

    // Skip ContactInfo and ProfessionalInfo for authenticated users
    if (!isAuthenticated) {
      steps.push(ContactInfo, ProfessionalInfo);

      if (hasOtherCredentialsStep) {
        steps.push(OtherCredentialsVerification);
      }
    }

    if (hasAccessoryStep) {
      steps.push(Accessories);
    }

    if (hasShippingStep) {
      steps.push(ShippingAddress);
    }

    steps.push(isAuthenticated ? ConfirmAuthenticatedContainer : ConfirmUnAuthenticatedContainer);

    return steps;
  }, [isAuthenticated, hasAccessoryStep, hasShippingStep, hasOtherCredentialsStep]);

  const stepLabels = useMemo(() => {
    if (isAuthenticated === undefined) return [];
    const labels = [];

    // Skip first two labels for authenticated users
    if (!isAuthenticated) {
      labels.push("Contact Info", "Professional Info");
    }

    if (hasAccessoryStep) {
      labels.push("Accessories");
    }

    if (hasShippingStep) {
      labels.push("Shipping Address");
    }

    labels.push("Confirm Purchase");

    return labels;
  }, [isAuthenticated, hasAccessoryStep, hasShippingStep]);

  const activeStepIndex = useMemo(() => {
    // Calculate stepNumber following the same logic as stepTitles
    let stepNumber = 0; // 0-indexed for array access

    // Track which absolute step number corresponds to which index
    const stepMapping: Record<number, number> = {};
    let absoluteStep = 1;

    // Skip first two steps for authenticated users
    if (!isAuthenticated) {
      // Contact Info - step 1
      stepMapping[absoluteStep] = stepNumber;
      absoluteStep++;
      stepNumber++;

      // Professional Info - step 2
      stepMapping[absoluteStep] = stepNumber - 1; // Still shows Professional Info
      absoluteStep++;

      // Other Credentials - step 3 (step 2.1 - still highlights Professional Info)
      if (hasOtherCredentialsStep) {
        stepMapping[absoluteStep] = stepNumber - 1; // Still shows Professional Info
        absoluteStep++;
      }
      // Now increment for next step
      stepNumber++;
    }

    // Accessories - conditional
    if (hasAccessoryStep) {
      stepMapping[absoluteStep] = stepNumber;
      absoluteStep++;
      stepNumber++;
    }

    // Shipping - conditional
    if (hasShippingStep) {
      stepMapping[absoluteStep] = stepNumber;
      absoluteStep++;
      stepNumber++;
    }

    // Confirm - always last
    stepMapping[absoluteStep] = stepNumber;

    // Return the index for the current step, defaulting to 0
    return stepMapping[currentStep] ?? 0;
  }, [currentStep, isAuthenticated, hasAccessoryStep, hasShippingStep, hasOtherCredentialsStep]);

  const stepTitles = useMemo(() => {
    const titles: Record<string, string> = {};
    let stepNumber = 1;

    // Skip first two steps for authenticated users
    if (!isAuthenticated) {
      // Contact Info - step 1
      titles.ContactInfo = `${stepNumber}. Please enter your contact info`;
      stepNumber++;

      // Professional Info - step 2
      titles.ProfessionalInfo = `${stepNumber}. Please select your professional credentials`;

      // Other Credentials - step 2.1
      if (hasOtherCredentialsStep) {
        titles.OtherCredentialsVerification = "2.1 Verify professional credentials";
      }
      stepNumber++;
    }

    // Accessories - conditional
    if (hasAccessoryStep) {
      if (isAuthenticated) {
        titles.Accessories = `Accessories`;
      } else {
        titles.Accessories = `${stepNumber}. Please select any optional accessories`;
      }
      stepNumber++;
    }

    // Shipping - conditional
    if (hasShippingStep) {
      if (isAuthenticated) {
        titles.ShippingAddress = `Shipping`;
      } else {
        titles.ShippingAddress = `${stepNumber}. Please enter your shipping address`;
      }
      stepNumber++;
    }

    // Confirm - always last
    if (isAuthenticated) {
      titles.ConfirmPurchase = `Confirm your purchase`;
    } else {
      titles.ConfirmPurchase = `${stepNumber}. Enter your billing info and confirm your purchase`;
    }

    return titles;
  }, [isAuthenticated, hasAccessoryStep, hasShippingStep, hasOtherCredentialsStep]);

  const authenticatedStepTitles = useMemo(() => {
    const titles: Record<string, string> = {};

    // Authenticated users skip first two steps, so we start with Accessories/Shipping

    // Accessories - conditional
    if (hasAccessoryStep) {
      titles.Accessories = "Accessories";
    }

    // Shipping - conditional
    if (hasShippingStep) {
      titles.ShippingAddress = "Shipping";
    }

    // Confirm - always last
    titles.ConfirmPurchase = "Confirm Your Purchase";
    titles.ConfirmAuthenticatedContainer = "Confirm Your Purchase";

    return titles;
  }, [hasAccessoryStep, hasShippingStep]);

  /**
   * Do not use: const isOnConfirmStep = currentStep === stepComponents.length;
   * - this breaks accessories and licenses page
   */
  const isOnConfirmStep = /confirm|billing/i.test(stepLabels[activeStepIndex] || "");

  return {
    hasAccessoryStep,
    hasShippingStep,
    stepComponents,
    stepLabels,
    stepTitles,
    authenticatedStepTitles,
    currentStep,
    activeStepIndex,
    isOnConfirmStep,
  };
};
